import { useCallback, useEffect, useState } from "react";
import {
	State,
	MoveData,
	UpdateData,
	ElementValidation,
} from "../../../hooks/FormDesigner/useElementList";
import { useTranslation } from "react-i18next";
import {
	DragDropContext,
	Draggable,
	Droppable,
	DropResult,
} from "react-beautiful-dnd";
import Element from "./Element";
import { ElementTypeList, PopulatesData } from "schemas/elements";
import { AlertProps, SearchSelectOptionProps } from "@alm/flow-ds/components";
import ElementsDialog from "../ElementsDialog";

type Props = {
	isEmu?: boolean;
	state: State;
	isNested?: boolean;
	legacySearchField?: SearchSelectOptionProps;
	hideMultiInputText?: boolean;
	nestedField?: "search";
	isSearchView?: boolean;
	tableNested?: boolean;
	validation?: boolean;
	addAlert?: (alert: string, status: AlertProps["status"]) => void;
	addElement: (data: ElementTypeList) => void;
	moveElement: (data: MoveData) => void;
	removeElement: (data: number) => void;
	updateElement: (data: UpdateData) => void;
	updateValidation: (data: boolean) => void;
	updateElementValidation: (data: ElementValidation) => void;
};

const ELEMENTS = [
	"text",
	"number",
	"select",
	"radio",
	"checkbox",
	"textarea",
	"date",
	"paragraph",
	"header",
];

function ElementList({
	isEmu,
	state,
	isNested = false,
	legacySearchField,
	nestedField,
	isSearchView,
	validation,
	addAlert,
	addElement,
	moveElement,
	removeElement,
	updateElement,
	updateValidation,
	updateElementValidation,
	hideMultiInputText,
	tableNested,
}: Props) {
	const { t } = useTranslation();

	const [populatesData, setPopulatesData] = useState<PopulatesData[]>([]);

	const onDragEnd = useCallback(
		(result: DropResult) => {
			if (!result.destination) return;
			const { source, destination } = result;
			moveElement({
				initial: source.index,
				destination: destination.index,
			});
		},
		[moveElement]
	);

	useEffect(() => {
		if (validation !== undefined && isNested && state.elements.length > 0) {
			updateValidation(validation);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [validation]);

	useEffect(() => {
		if (state.elements.length > 0) {
			const populatesDataTemp: PopulatesData[] = [];
			state.elements.forEach(element => {
				if (
					(element.elem === "reference" ||
						element.elem === "lookup") &&
					(element.name !== undefined ||
						element.search?.payload !== undefined)
				) {
					populatesDataTemp.push({
						from: element.name?.trim(),
						key: element.search?.payload?.["request-uri"],
					});
				}
			});
			setPopulatesData(populatesDataTemp);
		}
	}, [state.elements]);

	return (
		<>
			<div className="elements">
				<DragDropContext onDragEnd={result => onDragEnd(result)}>
					<div
						style={{
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
						}}
						key={"elementList"}>
						{state.elements.length > 0 ? (
							<Droppable
								droppableId={"elementList"}
								key={"elementList"}>
								{(provided, snapshot) => {
									return (
										<div
											{...provided.droppableProps}
											ref={provided.innerRef}
											className={
												snapshot.isDraggingOver
													? "element-list dragging"
													: "element-list"
											}>
											{state.elements.map(
												(item, index) => {
													return (
														<Draggable
															key={item.elementId}
															draggableId={
																item.elementId
															}
															index={index}>
															{(
																provided,
																snapshot
															) => {
																return (
																	<Element
																		isEmu={
																			isEmu
																		}
																		addAlert={
																			addAlert
																		}
																		removeElement={
																			removeElement
																		}
																		moveElement={
																			moveElement
																		}
																		updateElement={
																			updateElement
																		}
																		updateElementValidation={
																			updateElementValidation
																		}
																		index={
																			index
																		}
																		item={
																			item
																		}
																		formEntity={
																			state.formEntity
																		}
																		draggable={
																			provided.draggableProps
																		}
																		// @ts-expect-error 123
																		dragHandle={
																			provided.dragHandleProps
																		}
																		snapshot={
																			snapshot
																		}
																		// @ts-expect-error 123
																		innerRef={
																			provided.innerRef
																		}
																		lastChild={
																			index ===
																			state
																				.elements
																				.length -
																				1
																		}
																		populatesData={
																			ELEMENTS.includes(
																				item.elem
																			)
																				? populatesData
																				: undefined
																		}
																		legacySearchField={
																			legacySearchField
																		}
																		isSearchView={
																			isSearchView
																		}
																		hideMultiInputText={
																			hideMultiInputText
																		}
																		tableNested={
																			tableNested
																		}
																		fullWidth={
																			state
																				.formLayout
																				.fullWidth
																		}
																	/>
																);
															}}
														</Draggable>
													);
												}
											)}
											{provided.placeholder}
										</div>
									);
								}}
							</Droppable>
						) : (
							<div
								className={`element-list${
									validation &&
									!isNested &&
									state.formEntity.value !== ""
										? " invalid"
										: ""
								}`}>
								<div className={`no-elements`}>
									{isNested
										? nestedField === "search"
											? t(
													"formDesigner.addText.noViewElements"
											  )
											: t(
													"formDesigner.addText.noNestedElements"
											  )
										: t("formDesigner.addText.noElements")}
								</div>
							</div>
						)}
					</div>
				</DragDropContext>
			</div>
			<div className="element-list-buttons">
				<ElementsDialog
					isEmu={isEmu}
					newElement={addElement}
					isNested={isNested}
					nestedField={nestedField}
				/>
			</div>
		</>
	);
}

export default ElementList;
